import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  API_ORIGIN,
  HOROSCOPE_XP_ENABLED,
  resolveUserId,
} from '../utils/homeHelpers';
import { authenticatedRequest } from '../services/authApi';
import {
  horoscopeTodayCacheKey,
  legacyHoroscopeTodayCacheKey,
  normalizeHoroscopeLocale,
} from '../utils/horoscopeLocale';

const useHomeHoroscope = ({ user, token, resolvedUid, isAuthReady }) => {
  const { i18n } = useTranslation();
  const locale = normalizeHoroscopeLocale(i18n.resolvedLanguage || i18n.language);
  const [horoscope, setHoroscope] = useState(() => {
    try {
      const uid = resolveUserId(user);
      const cacheKey = horoscopeTodayCacheKey(uid, locale);
      const cached = localStorage.getItem(cacheKey)
        || (locale === 'es' ? localStorage.getItem(legacyHoroscopeTodayCacheKey(uid)) : null);
      if (!cached) return null;
      const parsed = JSON.parse(cached);
      return parsed && parsed.ok && (!parsed.locale || parsed.locale === locale) ? parsed : null;
    } catch {
      return null;
    }
  });

  const [horoOpen, setHoroOpen] = useState(false);

  useEffect(() => {
    if (!isAuthReady || !HOROSCOPE_XP_ENABLED || !token || !resolvedUid) return;

    let cancelled = false;
    const cacheKey = horoscopeTodayCacheKey(resolvedUid, locale);
    setHoroscope(null);
    setHoroOpen(false);

    try {
      const cached = localStorage.getItem(cacheKey)
        || (locale === 'es' ? localStorage.getItem(legacyHoroscopeTodayCacheKey(resolvedUid)) : null);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.ok && (!parsed.locale || parsed.locale === locale)) {
          setHoroscope(parsed);
        }
      }
    } catch {}

    authenticatedRequest(`${API_ORIGIN}/api/horoscope/today`, {
      rawResponse: true,
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Accept-Language': locale },
      diagSource: 'useHomeHoroscope',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.ok) {
          setHoroscope(data);
          setHoroOpen(false);
          try {
            window.__HORO_PREFETCH = data;
            localStorage.setItem(cacheKey, JSON.stringify(data));
          } catch {}
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isAuthReady, resolvedUid, token, locale]);

  const horoscopeLocale = horoscope?.locale || 'es';
  const localizedHoroscope = horoscopeLocale === locale ? horoscope : null;

  return { horoscope: localizedHoroscope, horoOpen, setHoroOpen };
};

export default useHomeHoroscope;
